/**
 * @fileoverview API-1820 (ruling R4/R5, FRE-1409) — the ONE definition of what
 * "Tonight", "Tomorrow", "This weekend", "This week", "Next 7 days" and
 * "Next 30 days" mean, in city-local time.
 *
 * Both `services/backend-api` (`/events/discover`, `/events/count`, every facet
 * pass) and `apps/web` (URL state, the wire `date` mapping, the results-bar and
 * hero labels, the day strip) import this module. Before it, the same windows
 * were hand-rolled three times — backend `discoverFacets.ts`, web
 * `dayStrip.ts`, web `dateKeywordMap.ts` — and each copy drifted on its own
 * (API-1814: "Next 7 days" meant next Monday–Sunday on one side only).
 *
 * ## Windows
 *
 * Every window is an absolute, inclusive `[start, end]` pair of instants. The
 * zone is the city's (`cityTimeZone(cityCode)`, a projection of
 * `CITY_RECORDS`) — never a literal.
 *
 * | Preset (URL) | Keyword (wire)  | Window                                                                 |
 * | ------------ | --------------- | ---------------------------------------------------------------------- |
 * | `tonight`    | `today`         | now → 04:59:59.999 local on the morning that ends tonight (see below)  |
 * | `tomorrow`   | `tomorrow`      | the next local calendar day, 00:00 → 23:59:59.999                      |
 * | `weekend`    | `this-weekend`  | Fri 17:00 → Sun 23:59:59.999 local, never starting before now          |
 * | `this-week`  | `this-week`     | now → Sun 23:59:59.999 local                                           |
 * | `next7`      | `next7`         | now → now + 7 × 24 h (absolute, so a DST change cannot shorten it)     |
 * | `next30`     | `next30`        | now → 23:59:59.999 local on the calendar date 30 days after today      |
 *
 * - **Tonight** ends at the 05:00 night boundary. Before 05:00 local the night
 *   in progress is the one that began yesterday evening, so at 00:30 "Tonight"
 *   is the rest of this night (ends 04:59:59.999 today), not tomorrow night.
 * - **This weekend** on Saturday or after Friday 17:00 starts now; on Sunday it
 *   is the rest of today; Monday–Thursday it is the coming Friday 17:00 →
 *   Sunday.
 * - **`next-week`** is a retired alias: the API still accepts it (bookmarks,
 *   old MCP prompts) and resolves it to `next7`. No UI offers it.
 *
 * ## Started events with no end time (R5)
 *
 * A started event with no `endDate` is still browsable for
 * {@link STARTED_WITHOUT_END_DEFAULT_MS} (one global 3 h default) and reads
 * "Started N min ago". The not-over clauses in backend-api and db-core read
 * this constant; there is no per-category default.
 *
 * ## Calendar math
 *
 * Calendar arithmetic is done on `YYYY-MM-DD` strings anchored at UTC midnight,
 * so a 23 h or 25 h DST day can neither skip nor repeat a date. Wall-clock →
 * instant conversion goes through `Intl` (no runtime dependency), and is pinned
 * against both 2026-11-01 and 2027-03-14 in `__tests__/dateWindows.test.ts`.
 */
import { cityTimeZone } from './cityTimeZone';

/** Wire keywords `/events/discover?date=` resolves, in UI order. */
export const DATE_WINDOW_KEYWORDS = [
  'today',
  'tomorrow',
  'this-weekend',
  'next7',
  'next30',
  'this-week',
] as const;
export type DateWindowKeyword = (typeof DATE_WINDOW_KEYWORDS)[number];

/**
 * Keywords the API still accepts but no surface offers, mapped onto the window
 * they resolve to.
 */
export const RETIRED_DATE_WINDOW_KEYWORDS = { 'next-week': 'next7' } as const satisfies Record<
  string,
  DateWindowKeyword
>;
export type RetiredDateWindowKeyword = keyof typeof RETIRED_DATE_WINDOW_KEYWORDS;

/** One preset as the UI names it: URL value, wire keyword, label. */
export interface DateWindowPresetDefinition {
  /** `?date=` value in a web URL. */
  readonly preset: DateWindowPreset;
  /** `?date=` value on `/events/discover`. */
  readonly keyword: DateWindowKeyword;
  /** Sentence-case label shared by chips, the hero and the results bar. */
  readonly label: string;
}

/** URL presets, in chip order. `this-week` is the nav/footer link, not a chip. */
export const DATE_WINDOW_PRESETS = [
  'tonight',
  'tomorrow',
  'weekend',
  'next7',
  'next30',
  'this-week',
] as const;
export type DateWindowPreset = (typeof DATE_WINDOW_PRESETS)[number];

const PRESET_DEFINITIONS: Readonly<Record<DateWindowPreset, DateWindowPresetDefinition>> = {
  tonight: { preset: 'tonight', keyword: 'today', label: 'Tonight' },
  tomorrow: { preset: 'tomorrow', keyword: 'tomorrow', label: 'Tomorrow' },
  weekend: { preset: 'weekend', keyword: 'this-weekend', label: 'This weekend' },
  next7: { preset: 'next7', keyword: 'next7', label: 'Next 7 days' },
  next30: { preset: 'next30', keyword: 'next30', label: 'Next 30 days' },
  'this-week': { preset: 'this-week', keyword: 'this-week', label: 'This week' },
};

/** True for a URL preset this module defines. */
export function isDateWindowPreset(value: string): value is DateWindowPreset {
  return Object.prototype.hasOwnProperty.call(PRESET_DEFINITIONS, value);
}

/** The definition for a URL preset. */
export function dateWindowPreset(preset: DateWindowPreset): DateWindowPresetDefinition {
  return PRESET_DEFINITIONS[preset];
}

/** Local hour at which "Tonight" ends (exclusive). */
export const TONIGHT_ENDS_LOCAL_HOUR = 5;
/** Local hour on Friday at which "This weekend" begins. */
export const WEEKEND_STARTS_LOCAL_HOUR = 17;
/** "Next 7 days": exactly 168 absolute hours. */
export const NEXT7_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
/** "Next 30 days": through the local calendar date this many days after today. */
export const NEXT30_LOCAL_DAYS = 30;
/**
 * R5 — how long a started event with no `endDate` stays browsable. One global
 * value; ruling R5 rejected per-category defaults.
 */
export const STARTED_WITHOUT_END_DEFAULT_MS = 3 * 60 * 60 * 1000;

/** An absolute, inclusive window. */
export interface DateWindow {
  readonly start: Date;
  readonly end: Date;
}

/** A parsed `?date=` value: a keyword, or an inclusive local-date range. */
export type DateWindowFilter =
  | { kind: 'keyword'; value: DateWindowKeyword | RetiredDateWindowKeyword }
  | { kind: 'range'; start: string; end: string };

const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_MINUTE = 60_000;

function utcAnchor(localDate: string): Date | null {
  const match = LOCAL_DATE.exec(localDate);
  if (!match) return null;
  const anchor = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  // `Date.UTC` rolls `2026-13-01` into January; only a round-trip is a real date.
  return anchor.toISOString().slice(0, 10) === localDate ? anchor : null;
}

/** True when `value` is a real `YYYY-MM-DD` calendar date. */
export function isLocalDateKey(value: string): boolean {
  return utcAnchor(value) !== null;
}

/** `YYYY-MM-DD` + `days` calendar days. Timezone-free, so DST cannot skew it. */
export function addLocalDays(localDate: string, days: number): string {
  const anchor = utcAnchor(localDate);
  if (!anchor) return localDate;
  anchor.setUTCDate(anchor.getUTCDate() + days);
  return anchor.toISOString().slice(0, 10);
}

/** `0` = Sunday … `6` = Saturday for a calendar date. */
export function localWeekday(localDate: string): number {
  return utcAnchor(localDate)?.getUTCDay() ?? 0;
}

const PARTS_FORMATTERS = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = PARTS_FORMATTERS.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    PARTS_FORMATTERS.set(timeZone, formatter);
  }
  return formatter;
}

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function wallClock(instant: Date, timeZone: string): WallClock {
  const parts = partsFormatter(timeZone).formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
}

/** Offset (local − UTC, in ms) of `timeZone` at `instant`. */
function zoneOffsetMs(instant: number, timeZone: string): number {
  const wholeSecond = Math.floor(instant / 1000) * 1000;
  const wall = wallClock(new Date(wholeSecond), timeZone);
  const asUtc = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second);
  return asUtc - wholeSecond;
}

/** The city-local calendar date (`YYYY-MM-DD`) of an instant. */
export function localDateKey(instant: Date, timeZone: string): string {
  const wall = wallClock(instant, timeZone);
  const month = String(wall.month).padStart(2, '0');
  const day = String(wall.day).padStart(2, '0');
  return `${String(wall.year).padStart(4, '0')}-${month}-${day}`;
}

/** The city-local hour (`0`–`23`) of an instant. */
export function localHour(instant: Date, timeZone: string): number {
  return wallClock(instant, timeZone).hour;
}

/**
 * The instant at which the wall clock in `timeZone` reads `localDate` at
 * `hour:minute:second.ms`. Every boundary this module asks for (00:00, 04:59,
 * 17:00, 23:59) exists exactly once on every day in every US zone, so the DST
 * gap and overlap (both at 01:00–03:00) are never asked about.
 */
export function localTimeToInstant(
  localDate: string,
  timeZone: string,
  hour = 0,
  minute = 0,
  second = 0,
  ms = 0,
): Date {
  const anchor = utcAnchor(localDate);
  if (!anchor) throw new RangeError(`Not a calendar date: ${localDate}`);
  const naive = Date.UTC(
    anchor.getUTCFullYear(),
    anchor.getUTCMonth(),
    anchor.getUTCDate(),
    hour,
    minute,
    second,
    ms,
  );
  // Two passes: the offset at the first guess can differ from the offset at
  // the answer when a DST transition falls between them.
  const firstGuess = naive - zoneOffsetMs(naive, timeZone);
  return new Date(naive - zoneOffsetMs(firstGuess, timeZone));
}

function localDayStart(localDate: string, timeZone: string): Date {
  return localTimeToInstant(localDate, timeZone, 0, 0, 0, 0);
}

function localDayEnd(localDate: string, timeZone: string): Date {
  return localTimeToInstant(localDate, timeZone, 23, 59, 59, 999);
}

function later(a: Date, b: Date): Date {
  return new Date(Math.max(a.getTime(), b.getTime()));
}

/** Resolve a retired alias onto the keyword it means. */
export function canonicalDateWindowKeyword(
  keyword: DateWindowKeyword | RetiredDateWindowKeyword,
): DateWindowKeyword {
  return keyword === 'next-week' ? RETIRED_DATE_WINDOW_KEYWORDS[keyword] : keyword;
}

/**
 * The window a wire keyword names, at `now`, in `timeZone`. See the table in
 * the file header.
 */
export function resolveDateWindowKeyword(
  keyword: DateWindowKeyword | RetiredDateWindowKeyword,
  timeZone: string,
  now: Date,
): DateWindow {
  const today = localDateKey(now, timeZone);
  const nowCopy = new Date(now.getTime());
  const canonical = canonicalDateWindowKeyword(keyword);

  switch (canonical) {
    case 'today': {
      const morning =
        localHour(now, timeZone) < TONIGHT_ENDS_LOCAL_HOUR ? today : addLocalDays(today, 1);
      return {
        start: nowCopy,
        end: localTimeToInstant(morning, timeZone, TONIGHT_ENDS_LOCAL_HOUR - 1, 59, 59, 999),
      };
    }
    case 'tomorrow': {
      const tomorrow = addLocalDays(today, 1);
      return { start: localDayStart(tomorrow, timeZone), end: localDayEnd(tomorrow, timeZone) };
    }
    case 'this-weekend': {
      const weekday = localWeekday(today);
      const sunday = addLocalDays(today, (7 - weekday) % 7);
      const friday = addLocalDays(sunday, -2);
      const fridayEvening = localTimeToInstant(friday, timeZone, WEEKEND_STARTS_LOCAL_HOUR);
      return { start: later(fridayEvening, nowCopy), end: localDayEnd(sunday, timeZone) };
    }
    case 'this-week': {
      const sunday = addLocalDays(today, (7 - localWeekday(today)) % 7);
      return { start: nowCopy, end: localDayEnd(sunday, timeZone) };
    }
    case 'next7':
      return { start: nowCopy, end: new Date(now.getTime() + NEXT7_DURATION_MS) };
    case 'next30':
      return {
        start: nowCopy,
        end: localDayEnd(addLocalDays(today, NEXT30_LOCAL_DAYS), timeZone),
      };
    default: {
      const exhaustive: never = canonical;
      throw new Error(`Unhandled date window keyword: ${String(exhaustive)}`);
    }
  }
}

/** An inclusive local-date range: first day 00:00 → last day 23:59:59.999. */
export function resolveLocalDateRangeWindow(
  startDate: string,
  endDate: string,
  timeZone: string,
): DateWindow {
  return { start: localDayStart(startDate, timeZone), end: localDayEnd(endDate, timeZone) };
}

/** The window a parsed `?date=` value names. */
export function resolveDateWindow(
  filter: DateWindowFilter,
  timeZone: string,
  now: Date,
): DateWindow {
  return filter.kind === 'range'
    ? resolveLocalDateRangeWindow(filter.start, filter.end, timeZone)
    : resolveDateWindowKeyword(filter.value, timeZone, now);
}

/** {@link resolveDateWindowKeyword} with the zone read off the city record. */
export function resolveCityDateWindowKeyword(
  keyword: DateWindowKeyword | RetiredDateWindowKeyword,
  cityCode: string,
  now: Date,
): DateWindow {
  return resolveDateWindowKeyword(keyword, cityTimeZone(cityCode), now);
}

/**
 * R5 — whole minutes since a started, end-less event began, while it is still
 * inside {@link STARTED_WITHOUT_END_DEFAULT_MS}; `null` when it has not started
 * or the default has run out. Drives "Started N min ago".
 */
export function minutesSinceStartWithoutEnd(startMs: number, nowMs: number): number | null {
  if (!Number.isFinite(startMs) || nowMs < startMs) return null;
  const elapsed = nowMs - startMs;
  if (elapsed >= STARTED_WITHOUT_END_DEFAULT_MS) return null;
  return Math.floor(elapsed / MS_PER_MINUTE);
}
